
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, emiratesOptions } from "./BaseForm";
import { PropertyAllRisksFormData, FormProps } from "@/types/forms";

type PropertyAllRisksFormProps = FormProps<PropertyAllRisksFormData>;

const PropertyAllRisksForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: PropertyAllRisksFormProps) => {
  const [formData, setFormData] = useState<PropertyAllRisksFormData>({
    propertyLocation: initialData.propertyLocation || "",
    ownOrLease: initialData.ownOrLease || "",
    sumInsured: initialData.sumInsured || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.propertyLocation) newErrors.propertyLocation = "Property location is required";
    if (!formData.ownOrLease) newErrors.ownOrLease = "Please specify if you own or lease";
    if (!formData.sumInsured) newErrors.sumInsured = "Sum insured amount is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof PropertyAllRisksFormData>(field: K, value: PropertyAllRisksFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Property Location (Emirate)" required>
        <Select value={formData.propertyLocation} onValueChange={(value) => updateField('propertyLocation', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select emirate" />
          </SelectTrigger>
          <SelectContent>
            {emiratesOptions.map((emirate) => (
              <SelectItem key={emirate} value={emirate}>
                {emirate}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.propertyLocation && <p className="text-red-500 text-sm">{errors.propertyLocation}</p>}
      </FormField>

      <FormField label="Do you own or lease the premises?" required>
        <Select value={formData.ownOrLease} onValueChange={(value) => updateField('ownOrLease', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="own">Own</SelectItem>
            <SelectItem value="lease">Lease</SelectItem>
          </SelectContent>
        </Select>
        {errors.ownOrLease && <p className="text-red-500 text-sm">{errors.ownOrLease}</p>}
      </FormField>

      <FormField label="Approximate total sum insured (AED)" required>
        <Input
          type="number"
          placeholder="Enter amount in AED"
          value={formData.sumInsured}
          onChange={(e) => updateField('sumInsured', e.target.value)}
        />
        {errors.sumInsured && <p className="text-red-500 text-sm">{errors.sumInsured}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default PropertyAllRisksForm;
